import { memo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Handle, Position, type NodeProps } from 'reactflow';
import { NODE_META, INPUT_TYPES, OUTPUT_TYPES } from '../nodeTypes';

// ── Shared handle className ─────────────────────────────────────────────────
const HANDLE_CLS = [
  '!w-3 !h-3 !rounded-full',
  '!bg-white !border-2 !border-zinc-300',
  'transition-all duration-150',
].join(' ');

// ── Spotlight hook ──────────────────────────────────────────────────────────
function useSpotlight() {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const onMouseMove = (e: React.MouseEvent) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setPos({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
  };
  return { ref, pos, onMouseMove };
}

// ── Component ───────────────────────────────────────────────────────────────
interface OperatorNodeData {
  label: string;
  config: { type: string; params: Record<string, unknown> };
  status?: 'idle' | 'running' | 'success' | 'error';
  readOnly?: boolean;
}

export const OperatorNode = memo(function OperatorNode({ data, type, selected }: NodeProps<OperatorNodeData>) {
  const nodeType = data.config?.type || type || 'filter';
  const meta = NODE_META[nodeType];
  const Icon = meta?.icon;
  const { ref, pos, onMouseMove } = useSpotlight();

  const isInput = INPUT_TYPES.has(nodeType);
  const isOutput = OUTPUT_TYPES.has(nodeType);

  const accentClass = meta?.bg.replace('-50', '-400') ?? 'bg-zinc-300';

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMouseMove}
      initial={{ scale: 0.88, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 220, damping: 22 }}
      className={[
        'relative group min-w-[160px] bg-white rounded-xl overflow-visible',
        'border-2 border-zinc-200',
        selected ? 'ring-2 ring-emerald-500 ring-offset-1' : '',
        'shadow-sm',
        'transition-colors duration-200',
      ].join(' ')}
    >
      {/* ── Top "input" handle (hidden for source nodes) ── */}
      {!isInput && (
        <Handle
          type="target"
          position={Position.Top}
          className={HANDLE_CLS}
          style={{ top: -6 }}
        />
      )}

      {/* Spotlight hover glow */}
      <div
        className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background: `radial-gradient(260px circle at ${pos.x}% ${pos.y}%, rgba(16,185,129,0.08), transparent 60%)`,
        }}
      />

      {/* Left accent strip */}
      {meta && <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-xl ${accentClass}`} />}

      {/* Content */}
      <div className="relative z-10 pl-3 pr-3 py-2.5 overflow-hidden rounded-xl">
        <div className="flex items-center gap-2.5">
          {/* Icon badge */}
          {Icon && meta && (
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.bg}`}>
              <Icon className={`w-4 h-4 ${meta.color}`} />
            </div>
          )}

          {/* Label */}
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-zinc-900 truncate leading-tight">{data.label}</p>
            <p className="text-[10px] text-zinc-400">{meta?.label || nodeType}</p>
          </div>
        </div>

        {/* Config params preview */}
        {data.config?.params && Object.keys(data.config.params).length > 0 && (
          <div className="mt-2 pt-2 border-t border-zinc-100 flex flex-wrap gap-1">
            {Object.entries(data.config.params).slice(0, 2).map(([key]) => (
              <span key={key} className="px-1.5 py-0.5 text-[9px] font-mono bg-zinc-100 text-zinc-500 rounded truncate max-w-[70px]">
                {key}
              </span>
            ))}
            {Object.keys(data.config.params).length > 2 && (
              <span className="px-1.5 py-0.5 text-[9px] text-zinc-400">
                +{Object.keys(data.config.params).length - 2}
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Bottom "output" handle (hidden for sink nodes) ── */}
      {!isOutput && (
        <Handle
          type="source"
          position={Position.Bottom}
          className={HANDLE_CLS}
          style={{ bottom: -6 }}
        />
      )}
    </motion.div>
  );
});

OperatorNode.displayName = 'OperatorNode';
